import React from 'react';
import { OperationsListProps, Operation } from '../types';
import modAliases from '../../../data/modAlias.json';

/**
 * Array of available operation types for policy modifications
 */
const AVAILABLE_OPERATIONS = ['addOrReplace', 'add', 'remove'] as const;

/**
 * OperationsList component for managing policy modification operations
 * 
 * This component provides an interface for defining and managing operations
 * that modify Azure resources. It supports:
 * - Adding/removing operations
 * - Selecting operation types
 * - Specifying fields with searchable dropdown
 * - Setting operation values
 * - Conditional operation configuration
 * - Inline editing of operations for a cleaner UX
 * 
 * @example
 * <OperationsList
 *   operations={currentOperations}
 *   onUpdateOperations={handleOperationsUpdate}
 * />
 */
export const OperationsList: React.FC<OperationsListProps> = ({
  operations,
  onAddOperation,
  onRemoveOperation,
  onOperationChange,
  updateFieldDropdownPosition,
  fieldSearchTerms,
  setFieldSearchTerms,
  isFieldDropdownOpen,
  setIsFieldDropdownOpen,
  fieldDropdownPositions,
  fieldDropdownRefs,
  fieldInputRefs
}) => {
  const [editingOperationIndex, setEditingOperationIndex] = React.useState<number | null>(null);

  const handleStartEdit = (index: number) => {
    setEditingOperationIndex(index);
  };

  const handleFinishEdit = () => {
    setEditingOperationIndex(null);
    // Potentially call a save handler here if needed, though onOperationChange updates live
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Operations</label>
      {operations.map((operation, index) => (
        <div key={index} className="mb-6 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Operation {index + 1}</h4>
            <div className="flex space-x-2">
              {editingOperationIndex !== index && (
                <button
                  onClick={() => handleStartEdit(index)}
                  className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-semibold p-1"
                  aria-label="Edit operation"
                >
                  ✏️ {/* Using an emoji as a placeholder for an icon */}
                </button>
              )}
              <button
                onClick={() => onRemoveOperation(index)}
                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-semibold p-1"
                aria-label="Remove operation"
              >
                ×
              </button>
            </div>
          </div>

          {editingOperationIndex === index ? (
            // EDITING VIEW: Show full form
            <div className="space-y-4">
              {/* Condition input */}
              <div>
                <label htmlFor={`condition-${index}`} className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Condition</label>
                <input
                  id={`condition-${index}`}
                  type="text"
                  value={operation.condition ?? ''}
                  onChange={(e) => onOperationChange(index, 'condition', e.target.value)}
                  placeholder="e.g. [greaterOrEquals(requestContext().apiVersion, '2019-04-01')]"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
                />
              </div>
              
              {/* Operation type select */}
              <div>
                <label htmlFor={`operation-type-${index}`} className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Operation Type</label>
                <select
                  id={`operation-type-${index}`}
                  value={operation.operation}
                  onChange={(e) => onOperationChange(index, 'operation', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="">Select Operation</option>
                  {AVAILABLE_OPERATIONS.map(op => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>

              {/* Field selection with searchable dropdown */}
              <div className="relative">
                <label htmlFor={`field-search-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Field</label>
                <input
                  id={`field-search-${index}`}
                  ref={(el) => { fieldInputRefs.current[index] = el; }}
                  type="text"
                  value={isFieldDropdownOpen[index] ? (fieldSearchTerms[index] || '') : operation.field}
                  onChange={(e) => {
                    if (isFieldDropdownOpen[index]) {
                      setFieldSearchTerms({ ...fieldSearchTerms, [index]: e.target.value });
                    } else {
                      onOperationChange(index, 'field', e.target.value);
                    }
                  }}
                  onClick={() => {
                    setIsFieldDropdownOpen({ ...isFieldDropdownOpen, [index]: !isFieldDropdownOpen[index] });
                    updateFieldDropdownPosition(index);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setIsFieldDropdownOpen({ ...isFieldDropdownOpen, [index]: false });
                      if (fieldSearchTerms[index] && operation.field !== fieldSearchTerms[index]) {
                        onOperationChange(index, 'field', fieldSearchTerms[index]);
                      }
                    }, 200);
                  }}
                  placeholder="Search fields or enter custom value..."
                  className="block w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-base text-gray-800 dark:text-gray-200 shadow-sm focus:outline-none focus:border-blue-500 focus:ring focus:ring-blue-500"
                />
                {isFieldDropdownOpen[index] && (
                  <div
                    className="fixed mt-1 w-full max-h-72 overflow-y-auto bg-gray-800 text-gray-200 dark:bg-gray-900 dark:text-gray-100 border border-gray-700 rounded shadow-sm z-50"
                    style={{
                      position: 'fixed',
                      top: `${fieldDropdownPositions[index]?.top || 0}px`,
                      left: `${fieldDropdownPositions[index]?.left || 0}px`,
                      width: fieldInputRefs.current[index]?.offsetWidth,
                    }}
                  >
                    {modAliases
                      .filter(alias => alias.toLowerCase().includes((fieldSearchTerms[index] || '').toLowerCase()))
                      .map(alias => (
                        <div
                          key={alias}
                          className="px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                          onMouseDown={() => {
                            const clean = alias.split(' (')[0];
                            onOperationChange(index, 'field', clean);
                            setFieldSearchTerms({ ...fieldSearchTerms, [index]: '' });
                            setIsFieldDropdownOpen({ ...isFieldDropdownOpen, [index]: false });
                          }}
                        >
                          {alias}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Value input */}
              <div>
                <label htmlFor={`value-${index}`} className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Value</label>
                <textarea
                  id={`value-${index}`}
                  value={
                    typeof operation.value === 'string'
                      ? operation.value
                      : JSON.stringify(operation.value, null, 2)
                  }
                  onChange={(e) => onOperationChange(index, 'value', e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (val.startsWith('[')) {
                      try {
                        const parsed = JSON.parse(val);
                        if (Array.isArray(parsed)) {
                          onOperationChange(index, 'value', parsed);
                        }
                      } catch { /* invalid JSON, leave as string */ }
                    }
                  }}
                  placeholder='Value or JSON array e.g., ["value1", "value2"]'
                  rows={3}
                  className="mt-1 block w-full pl-3 pr-3 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm resize-y"
                />
              </div>
              <button
                onClick={handleFinishEdit}
                className="mt-3 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              >
                Done Editing
              </button>
            </div>
          ) : (
            // NON-EDITING VIEW: Show summary (or current full view if summary is not yet designed)
            // For now, let's just show a placeholder or minimal info if not editing.
            // Or, we can choose to show the same detailed view but just without the "Done Editing" button
            // and with the edit button visible at the top.
            // Current implementation will hide the form inputs if not editing.
            // To show a summary:
            <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
              <p><strong>Condition:</strong> {operation.condition || <span className="italic text-gray-500 dark:text-gray-400">Not set</span>}</p>
              <p><strong>Operation:</strong> {operation.operation || <span className="italic text-gray-500 dark:text-gray-400">Not set</span>}</p>
              <p><strong>Field:</strong> {operation.field || <span className="italic text-gray-500 dark:text-gray-400">Not set</span>}</p>
              <p><strong>Value:</strong> {typeof operation.value === 'string' ? operation.value : JSON.stringify(operation.value) || <span className="italic text-gray-500 dark:text-gray-400">Not set</span>}</p>
            </div>
          )}
        </div>
      ))}
      
      <button
        onClick={() => onAddOperation()}
        className="mt-4 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
      >
        Add Operation
      </button>
    </div>
  );
}; 